import { ArrowRight, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import HeroDemoCard from '../components/home/HeroDemoCard'
import FeatureGrid from '../components/home/FeatureGrid'

/**
 * 공개 랜딩.
 *
 * claude.ai/design 의 Home 구성을 옮겼다. 머리글·바닥글은 앱 레이아웃이
 * 이미 들고 있어 본문만 둔다.
 */

const REGISTER_URL = 'https://app.shorty.kr/register'
const PRICING_URL = 'https://app.shorty.kr/price'

export default function HomePage() {
  const { t } = useTranslation()

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* 히어로 */}
      <section className="relative overflow-hidden bg-white dark:bg-gray-900">
        {/* 빛 번짐 두 겹 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 dark:opacity-30"
          style={{
            background:
              'radial-gradient(60% 80% at 85% 10%, #dbeafe 0%, rgba(219,234,254,0) 60%), radial-gradient(40% 60% at 0% 100%, #eef2ff 0%, rgba(238,242,255,0) 70%)',
          }}
        />
        {/* 점 격자 — 아래로 갈수록 사라진다 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-20"
          style={{
            backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            WebkitMaskImage: 'linear-gradient(180deg,#000,transparent 85%)',
            maskImage: 'linear-gradient(180deg,#000,transparent 85%)',
          }}
        />

        <div className="relative mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-14 px-6 pb-24 pt-20 lg:grid-cols-2">
          <div className="flex flex-col items-start gap-6">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-white px-3 py-1.5 text-[13px] font-semibold text-primary-700 shadow-sm dark:border-primary-900 dark:bg-gray-800 dark:text-primary-300">
              <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              {t('home.badge')}
            </span>

            <h1 className="text-[clamp(2.75rem,6vw,4.25rem)] font-extrabold leading-[1.12] tracking-[-0.02em] text-gray-900 dark:text-white">
              {t('home.heroLine1')}
              <br />
              <span className="text-primary-600 dark:text-primary-400">
                {t('home.heroLine2')}
              </span>
            </h1>

            <p className="max-w-[440px] text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              {t('home.heroDescription')}
            </p>

            <div className="mt-1 flex flex-wrap gap-2.5">
              <a
                href={REGISTER_URL}
                className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-5 py-3.5 text-base font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition hover:bg-primary-700"
              >
                {t('home.startFree')}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={PRICING_URL}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3.5 text-base font-semibold text-gray-900 transition hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:border-gray-600"
              >
                {t('home.seePricing')}
              </a>
            </div>
          </div>

          <HeroDemoCard />
        </div>
      </section>

      {/* 기능 */}
      <section className="mx-auto flex max-w-[1200px] flex-col gap-10 px-6 py-24">
        <h2 className="text-[clamp(1.75rem,3.4vw,2.375rem)] font-extrabold tracking-tight text-gray-900 dark:text-white">
          {t('home.featuresTitle')}
        </h2>
        <FeatureGrid />
      </section>

      {/* 시작하기 */}
      <section className="mx-auto max-w-[1200px] px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-600 via-[#3b4fe0] to-indigo-600 px-8 py-14 sm:px-12">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -top-32 h-[360px] w-[360px] rounded-full border border-white/20"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-10 right-10 h-[200px] w-[200px] rounded-full border border-white/[0.14]"
          />

          <div className="relative flex flex-wrap items-center justify-between gap-8">
            <div className="flex flex-col gap-2.5">
              <h2 className="text-[clamp(1.75rem,3.4vw,2.375rem)] font-extrabold tracking-tight text-white">
                {t('home.ctaTitle')}
              </h2>
              <p className="text-[17px] text-indigo-100">
                {t('home.ctaDescription')}
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5">
              <a
                href={REGISTER_URL}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3.5 text-base font-bold text-primary-700 transition hover:bg-primary-50"
              >
                {t('home.startFree')}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={PRICING_URL}
                className="inline-flex items-center rounded-xl border border-white/50 px-5 py-3.5 text-base font-semibold text-white transition hover:bg-white/10"
              >
                {t('home.seePricing')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
